export const MARKER_ASPECT = 16 / 9;
export const MARKER_OUTPUT_WIDTH = 1920;

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", () =>
      reject(new Error("No se pudo leer la imagen seleccionada"))
    );
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getOutputFormat = (type) => {
  if (type === "image/png") {
    return { mimeType: "image/png", extension: "png", quality: undefined };
  }

  if (type === "image/webp") {
    return { mimeType: "image/webp", extension: "webp", quality: 0.92 };
  }

  return { mimeType: "image/jpeg", extension: "jpg", quality: 0.92 };
};

const buildFileName = (originalName, extension) => {
  const base = (originalName || "imagen").replace(/\.[^.]+$/, "");

  return `${base}.${extension}`;
};

/**
 * Genera un File con la proporción del área recortada (16:9 por defecto).
 *
 * @param {string} imageSrc      objectURL de la imagen original
 * @param {object} pixelCrop     { x, y, width, height } que entrega react-easy-crop
 * @param {number} rotation      grados de rotación aplicados en el editor
 * @param {object} options       { name, type, maxWidth, aspect }
 * @returns {Promise<File>}
 */
export const getCroppedImageFile = async (
  imageSrc,
  pixelCrop,
  rotation = 0,
  options = {}
) => {
  if (!pixelCrop?.width || !pixelCrop?.height) {
    throw new Error("No hay un área de recorte válida");
  }

  const image = await createImage(imageSrc);
  const radians = (rotation * Math.PI) / 180;

  const boxWidth =
    Math.abs(Math.cos(radians) * image.width) +
    Math.abs(Math.sin(radians) * image.height);
  const boxHeight =
    Math.abs(Math.sin(radians) * image.width) +
    Math.abs(Math.cos(radians) * image.height);

  const rotatedCanvas = document.createElement("canvas");
  const rotatedCtx = rotatedCanvas.getContext("2d");

  rotatedCanvas.width = Math.ceil(boxWidth);
  rotatedCanvas.height = Math.ceil(boxHeight);

  rotatedCtx.translate(boxWidth / 2, boxHeight / 2);
  rotatedCtx.rotate(radians);
  rotatedCtx.translate(-image.width / 2, -image.height / 2);
  rotatedCtx.drawImage(image, 0, 0);

  const { mimeType, extension, quality } = getOutputFormat(options.type);
  const maxWidth = options.maxWidth || MARKER_OUTPUT_WIDTH;
  const aspect = options.aspect || MARKER_ASPECT;

  const outputWidth = Math.max(
    1,
    Math.round(Math.min(maxWidth, pixelCrop.width))
  );
  const outputHeight = Math.max(1, Math.round(outputWidth / aspect));

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = outputWidth;
  canvas.height = outputHeight;

  if (mimeType === "image/jpeg") {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, outputWidth, outputHeight);
  }

  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    rotatedCanvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputWidth,
    outputHeight
  );

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) {
          resolve(result);
          return;
        }

        reject(new Error("No se pudo generar la imagen recortada"));
      },
      mimeType,
      quality
    );
  });

  return new File([blob], buildFileName(options.name, extension), {
    type: mimeType,
    lastModified: Date.now(),
  });
};
